/**
 * OliveUp Pre-qualification rules engine
 * Pure function — instant client-side estimate based on time in business,
 * monthly revenue, and requested amount. No external calls.
 */

// Parse time-in-business option keys to months
const tibToMonths = {
  "<6 months": 3,
  "6-12 months": 9,
  "1-2 years": 18,
  "2-5 years": 36,
  "5+ years": 72,
};

// Parse monthly revenue option keys to a representative midpoint (USD/mo)
const revToMid = {
  "<10k": 5_000,
  "10-25k": 17_500,
  "25-75k": 50_000,
  "75-250k": 162_500,
  "250k+": 350_000,
};

// Parse requested amount option keys to a midpoint (USD)
const reqToMid = {
  "<25k": 15_000,
  "25-100k": 60_000,
  "100-500k": 300_000,
  "500k-1m": 750_000,
  "1m+": 1_500_000,
};

// Product catalog used for matching (display + simple eligibility rules)
const CATALOG = [
  {
    id: "sba_loans",
    title: "SBA Loans",
    minMonths: 24,
    minRev: 10_000,
    cap: 5_000_000,
    rateBase: [6, 13],
  },
  {
    id: "Commercial Loans",
    title: "Commercial Loans",
    minMonths: 6,
    minRev: 8_000,
    cap: 500_000,
    rateBase: [12, 30],
  },
  {
    id: "line_of_credit",
    title: "Lines of Credit",
    minMonths: 12,
    minRev: 15_000,
    cap: 250_000,
    rateBase: [10, 24],
  },
  {
    id: "equipment_financing",
    title: "Equipment Financing",
    minMonths: 3,
    minRev: 5_000,
    cap: 2_000_000,
    rateBase: [8, 22],
  },
  {
    id: "refinancing",
    title: "Refinancing",
    minMonths: 6,
    minRev: 10_000,
    cap: 5_000_000,
    rateBase: [7, 20],
  },
  {
    id: "working_capital",
    title: "Working Capital",
    minMonths: 6,
    minRev: 8_000,
    cap: 500_000,
    rateBase: [10, 26],
  },
  {
    id: "invoice_factoring",
    title: "Invoice Factoring",
    minMonths: 3,
    minRev: 8_000,
    cap: 5_000_000,
    rateBase: [9, 22],
  },
];

function fmt(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

/**
 * @param {{time_in_business: string, monthly_revenue: string, requested_amount: string}} input
 */
export function prequalify(input) {
  const months = tibToMonths[input.time_in_business] ?? 0;
  const rev = revToMid[input.monthly_revenue] ?? 0;
  const req = reqToMid[input.requested_amount] ?? 0;

  // Match products
  const matched = CATALOG.filter(
    (p) => months >= p.minMonths && rev >= p.minRev,
  );

  // Estimated max funding: 8x monthly revenue capped at requested amount * 1.5
  const revBased = rev * 8;
  const reqBased = req * 1.5;
  const estimatedMax = Math.max(
    Math.min(revBased, reqBased) || revBased,
    req || 0,
  );

  // Rate range — better profile = tighter band
  let rateLow = 18;
  let rateHigh = 32;
  if (months >= 24 && rev >= 25_000) {
    rateLow = 6;
    rateHigh = 16;
  } else if (months >= 12 && rev >= 15_000) {
    rateLow = 9;
    rateHigh = 22;
  } else if (months >= 6 && rev >= 10_000) {
    rateLow = 11;
    rateHigh = 26;
  }

  // Confidence tier — drives the headline copy
  let tier = "match";
  if (matched.length >= 5 && months >= 24 && rev >= 25_000) tier = "premium";
  else if (matched.length >= 3) tier = "strong";
  else if (matched.length === 0) tier = "review";

  return {
    tier,
    matched: matched.map((p) => p.title),
    estimatedMax,
    estimatedMaxLabel: estimatedMax > 0 ? fmt(estimatedMax) : "—",
    rateLow,
    rateHigh,
    months,
    revenue: rev,
    requested: req,
  };
}
